/**
 * AuthClient Service - Authentication abstraction layer
 *
 * Uses MSAL Browser for Microsoft OAuth authentication in web apps.
 */

import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserCacheLocation
} from '@azure/msal-browser';
import { logger } from '../utils/logger';

export interface AuthStatus {
  authenticated: boolean;
  user?: {
    id: string;
    name?: string;
    email: string;
    company?: string;
    is_admin?: boolean;
    isactive?: boolean;
  };
}

export interface LoginResult {
  success: boolean;
  error?: string;
  status?: AuthStatus;
  reason?: string;
  adminConsentUrl?: string;
  tenantId?: string;
}

export type AuthChangeCallback = (status: AuthStatus) => void;

/**
 * AuthClient interface - contract for authentication operations
 */
export interface IAuthClient {
  getStatus(): Promise<AuthStatus>;
  beginLogin(): Promise<LoginResult>;
  logout(localOnly?: boolean): Promise<void>;
  onAuthChanged(callback: AuthChangeCallback): () => void;
  getAccessToken(scopes?: string[]): Promise<string | null>;
}

// MSAL Configuration - Uses same env var names as Electron version (with VITE_ prefix)
const clientId = import.meta.env.VITE_MSAL_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_MSAL_TENANT_ID || 'organizations';
const redirectUri = import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin;

logger.log('[AuthClient] MSAL Config:', {
  clientId: clientId ? `${clientId.substring(0, 8)}...` : 'MISSING',
  tenantId,
  redirectUri
});

if (!clientId) {
  logger.error('[AuthClient] VITE_MSAL_CLIENT_ID is not set!');
}

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: window.location.origin
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false
  }
};

// Parse scopes from env (comma-separated)
const allScopes: string[] = (import.meta.env.VITE_MSAL_SCOPES || 'openid,profile,email,User.Read').split(',').map(s => s.trim());
// Login scopes: only OpenID Connect scopes (no api:// or Graph scopes for login)
const loginScopes = allScopes.filter((s: string) => !s.startsWith('api://') && !s.includes('.'));
// API scopes: only api:// scopes for backend calls
const apiScopes = allScopes.filter((s: string) => s.startsWith('api://'));
// Graph scopes: for Microsoft Graph API calls (like profile photo)
const graphScopes = allScopes.filter((s: string) => s.includes('.') && !s.startsWith('api://'));

logger.log('[AuthClient] Scopes:', { loginScopes, apiScopes, graphScopes });

if (apiScopes.length === 0) {
  logger.error('[AuthClient] WARNING: No api:// scopes found! Backend API calls will fail. Check VITE_MSAL_SCOPES in .env');
}

/**
 * BrowserAuthClient - MSAL Browser implementation
 */
export class BrowserAuthClient implements IAuthClient {
  private msalInstance: PublicClientApplication;
  private listeners: Set<AuthChangeCallback> = new Set();
  private initialized: boolean = false;
  private initPromise: Promise<void>;
  private cachedUserProfile: AuthStatus['user'] | null = null;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.initPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      logger.log('[AuthClient] Starting MSAL initialization...');
      await this.msalInstance.initialize();
      logger.log('[AuthClient] MSAL initialized');

      // Handle redirect response
      logger.log('[AuthClient] Checking for redirect response...');
      const response = await this.msalInstance.handleRedirectPromise();
      logger.log('[AuthClient] Redirect response:', response ? 'found' : 'none');

      if (response) {
        this.msalInstance.setActiveAccount(response.account);
        await this.fetchUserProfile();
        this.notifyListeners();
      } else {
        // Check for existing account
        const accounts = this.msalInstance.getAllAccounts();
        logger.log('[AuthClient] Existing accounts:', accounts.length);
        if (accounts.length > 0) {
          this.msalInstance.setActiveAccount(accounts[0]);
          await this.fetchUserProfile();
        }
      }

      this.initialized = true;
      logger.log('[AuthClient] Initialization complete');
    } catch (error) {
      logger.error('[AuthClient] MSAL initialization failed:', error);
      this.initialized = true;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initPromise;
    }
  }

  private async fetchUserProfile(): Promise<void> {
    try {
      logger.log('[AuthClient] Fetching user profile...');

      // Get token directly without going through getAccessToken (to avoid deadlock during init)
      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        logger.log('[AuthClient] No active account for profile fetch');
        return;
      }

      let token: string | null = null;
      try {
        const result = await this.msalInstance.acquireTokenSilent({
          scopes: apiScopes,
          account
        });
        token = result.accessToken;
      } catch (error) {
        logger.log('[AuthClient] Silent token acquisition failed during init, skipping profile fetch');
        return;
      }

      if (!token) {
        logger.log('[AuthClient] No token available for profile fetch');
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_CORPUS_API_BASE_URL || '';
      logger.log('[AuthClient] Calling /me endpoint at:', apiBaseUrl);
      const response = await fetch(`${apiBaseUrl}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        logger.log('[AuthClient] User profile fetched:', data.email);
        this.cachedUserProfile = {
          id: data.id,
          name: data.name || data.email?.split('@')[0],
          email: data.email,
          company: data.company,
          is_admin: data.is_admin,
          isactive: data.isactive
        };
      } else {
        logger.log('[AuthClient] Profile fetch failed:', response.status);
      }
    } catch (error) {
      logger.error('[AuthClient] Failed to fetch user profile:', error);
    }
  }

  async getStatus(): Promise<AuthStatus> {
    logger.log('[AuthClient] getStatus called, waiting for initialization...');
    await this.ensureInitialized();
    logger.log('[AuthClient] Initialization done, checking account...');

    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      logger.log('[AuthClient] No active account');
      return { authenticated: false };
    }
    logger.log('[AuthClient] Active account found:', account.username);

    // Use cached profile if available, otherwise use MSAL account info
    if (this.cachedUserProfile) {
      return {
        authenticated: true,
        user: this.cachedUserProfile
      };
    }

    return {
      authenticated: true,
      user: {
        id: account.localAccountId,
        name: account.name || account.username?.split('@')[0],
        email: account.username || ''
      }
    };
  }

  async beginLogin(): Promise<LoginResult> {
    await this.ensureInitialized();

    try {
      // Try silent login first
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          const silentResult = await this.msalInstance.acquireTokenSilent({
            scopes: loginScopes,
            account: accounts[0]
          });
          this.msalInstance.setActiveAccount(silentResult.account);
          await this.fetchUserProfile();
          this.notifyListeners();

          return {
            success: true,
            status: await this.getStatus()
          };
        } catch (silentError) {
          if (!(silentError instanceof InteractionRequiredAuthError)) {
            throw silentError;
          }
        }
      }

      // Interactive login with redirect (more reliable than popup due to COOP restrictions)
      await this.msalInstance.loginRedirect({
        scopes: loginScopes,
        prompt: 'select_account'
      });

      // loginRedirect will redirect away, so this code won't execute
      // The result is handled in handleRedirectPromise() during initialization
      return {
        success: true,
        status: await this.getStatus()
      };
    } catch (error: any) {
      logger.error('Login failed:', error);

      // Handle consent required
      if (error.errorCode === 'consent_required' || error.message?.includes('AADSTS65001')) {
        const tenantId = this.msalInstance.getActiveAccount()?.tenantId || import.meta.env.VITE_AZURE_TENANT_ID;
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        const adminConsentUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${clientId}`;

        return {
          success: false,
          error: 'Admin consent is required for this application.',
          reason: 'CONSENT_REQUIRED',
          adminConsentUrl,
          tenantId
        };
      }

      return {
        success: false,
        error: error.message || 'Login failed',
        reason: error.errorCode
      };
    }
  }

  async logout(localOnly: boolean = false): Promise<void> {
    await this.ensureInitialized();

    this.cachedUserProfile = null;

    if (localOnly) {
      // Just clear the active account - MSAL handles cache internally
      this.msalInstance.setActiveAccount(null);
      // Clear browser storage for this app
      const accounts = this.msalInstance.getAllAccounts();
      for (const acc of accounts) {
        // Use clearCache which is the proper way to remove cached tokens
        await this.msalInstance.clearCache({
          account: acc
        });
      }
    } else {
      // Full logout with redirect (more reliable than popup due to COOP restrictions)
      await this.msalInstance.logoutRedirect();
    }

    this.notifyListeners();
  }

  async getAccessToken(scopes: string[] = apiScopes): Promise<string | null> {
    await this.ensureInitialized();

    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes,
        account
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Use redirect for interactive token acquisition (more reliable due to COOP restrictions)
        await this.msalInstance.acquireTokenRedirect({ scopes });
        // Redirect will happen, return null for now - token will be available after redirect
        return null;
      }
      logger.error('Silent token acquisition failed:', error);
      return null;
    }
  }

  onAuthChanged(callback: AuthChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    this.getStatus().then(status => {
      this.listeners.forEach(callback => callback(status));
    });
  }
}

/**
 * MockAuthClient - Test/development implementation
 */
export class MockAuthClient implements IAuthClient {
  private authenticated: boolean;
  private mockUser = {
    id: 'mock-user-123',
    name: 'Test User',
    email: 'test.user@example.com'
  };
  private listeners: Set<AuthChangeCallback> = new Set();

  constructor(initialAuthState = false) {
    this.authenticated = initialAuthState;
  }

  async getStatus(): Promise<AuthStatus> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return {
      authenticated: this.authenticated,
      user: this.authenticated ? this.mockUser : undefined
    };
  }

  async beginLogin(): Promise<LoginResult> {
    await new Promise(resolve => setTimeout(resolve, 500));
    this.authenticated = true;
    const status = await this.getStatus();
    this.listeners.forEach(callback => callback(status));
    return { success: true, status };
  }

  async logout(): Promise<void> {
    this.authenticated = false;
    const status: AuthStatus = { authenticated: false };
    this.listeners.forEach(callback => callback(status));
  }

  async getAccessToken(): Promise<string | null> {
    return this.authenticated ? 'mock-token-12345' : null;
  }

  onAuthChanged(callback: AuthChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  setMockAuthenticated(authenticated: boolean): void {
    const changed = this.authenticated !== authenticated;
    this.authenticated = authenticated;
    if (changed) {
      const status: AuthStatus = {
        authenticated,
        user: authenticated ? this.mockUser : undefined
      };
      this.listeners.forEach(callback => callback(status));
    }
  }
}

/**
 * Default authClient instance
 * Uses BrowserAuthClient by default, MockAuthClient if VITE_BYPASS_LOGIN=true
 */
const bypassLogin = import.meta.env.VITE_BYPASS_LOGIN === 'true';
export const authClient: IAuthClient = bypassLogin
  ? new MockAuthClient(true)
  : new BrowserAuthClient();

export function createMockAuthClient(authenticated = false): MockAuthClient {
  return new MockAuthClient(authenticated);
}
